import {
  Grid,
  Menu,
  MenuItem,
  IconButton,
  Popover,
  Box,
  Typography,
  TextField
} from "@mui/material";
import React, { useState } from "react";
import { useDispatch } from "react-redux";
import { useParams } from "react-router-dom";
import HistoryIcon from "@mui/icons-material/HistoryOutlined";
import CheckIcon from "@mui/icons-material/Check";
import WarningAmberIcon from "@mui/icons-material/WarningAmber";
import KeyboardArrowDownIcon from "@mui/icons-material/KeyboardArrowDown";
import EditOutlinedIcon from "@mui/icons-material/EditOutlined";
import {
  ConfirmFormModal,
  DrawStage,
  useStylesStatusUpdate
} from "@toorak/tc-common-fe-sdk";
import { BankDetails } from "./BankDetails";
import { useDrawsStyles } from "../Draws.styles";
import {
  isOrigUser,
  isDrawAMRole,
  canViewWireVerification
} from "../../../utils/AccessManagement";
import {
  patchWireVerificationStatus,
  getWireVerificationHistory
} from "../../../stores/drawRequest/drawRequest.action";
import { getDrawDateCreated } from "../../AssetManagement.utils";

interface WireVerificationHistoryItem {
  historyId: number;
  drawId: number;
  previousStatus: string;
  newStatus: string;
  comment: string;
  updatedBy: string;
  createdAt: string;
}

const WireVerificationControl = ({
  status,
  drawId,
  originatorName,
  setSnackBarMsg,
  setOpenSnackBar,
  isDraft
}: {
  status?: string;
  drawId: string;
  originatorName?: string;
  setSnackBarMsg: (msg: { messageType: "success" | "info" | "warning" | "error" | undefined; message: string }) => void;
  setOpenSnackBar: (open: boolean) => void;
  isDraft: boolean;
}) => {
  const classes = useDrawsStyles();
  const classesLoc = useStylesStatusUpdate();
  const dispatch = useDispatch();
  const isCompleted = status === "Completed";
  const currentStatus = isCompleted ? "Completed" : "Pending";

  // Menu state
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const menuOpen = Boolean(anchorEl);

  // Confirmation modal state
  const [confirmModalOpen, setConfirmModalOpen] = useState(false);
  const [pendingStatus, setPendingStatus] = useState<"Pending" | "Completed" | null>(null);
  const [comment, setComment] = useState("");
  const [isSubmitting, setIsSubmitting] = useState(false);

  // History popover state
  const [historyAnchorEl, setHistoryAnchorEl] = useState<null | HTMLElement>(null);
  const historyOpen = Boolean(historyAnchorEl);
  const [historyData, setHistoryData] = useState<WireVerificationHistoryItem[]>([]);
  const [historyLoading, setHistoryLoading] = useState(false);

  const handleChipClick = (event: React.MouseEvent<HTMLElement>) => {
    if (isDraft) return; // Don't allow status change in draft mode
    setAnchorEl(event.currentTarget);
  };

  const handleMenuClose = () => {
    setAnchorEl(null);
  };

  const handleStatusSelect = (newStatus: "Pending" | "Completed") => {
    handleMenuClose();
    if (newStatus === currentStatus) return;
    setPendingStatus(newStatus);
    setComment("");
    setConfirmModalOpen(true);
  };

  const handleConfirmStatusChange = async () => {
    if (!pendingStatus || isSubmitting) return;
    setIsSubmitting(true);
    setConfirmModalOpen(false);
    try {
      await dispatch(patchWireVerificationStatus(drawId, pendingStatus, comment));
      setSnackBarMsg({
        messageType: "success",
        message: `Wire verification status updated to ${pendingStatus}`
      });
      setOpenSnackBar(true);
    } catch (err) {
      setSnackBarMsg({
        messageType: "error",
        message: "Failed to update wire verification status"
      });
      setOpenSnackBar(true);
    }
    setIsSubmitting(false);
    setPendingStatus(null);
    setComment("");
  };

  const handleHistoryClick = async (event: React.MouseEvent<HTMLElement>) => {
    setHistoryAnchorEl(event.currentTarget);
    setHistoryLoading(true);
    try {
      const history = await getWireVerificationHistory(drawId);
      setHistoryData(history);
    } catch (err) {
      console.error("Failed to fetch history:", err);
      setHistoryData([]);
    }
    setHistoryLoading(false);
  };

  const handleHistoryClose = () => {
    setHistoryAnchorEl(null);
  };

  const StatusIcon = isCompleted ? CheckIcon : WarningAmberIcon;

  const confirmModalBody = () => (
    <Box sx={{ padding: "0 20px 16px", minWidth: 200 }}>
      <Typography sx={{ mb: 2 }}>
        Are you sure you want to <strong>change</strong> the Wire verification status
        from <strong>{currentStatus}</strong> → <strong>{pendingStatus}</strong>?
      </Typography>
      <TextField
        id="wire-verification-comment"
        fullWidth
        multiline
        rows={3}
        placeholder="Add Comment *"
        value={comment}
        onChange={(e) => setComment(e.target.value)}
        variant="outlined"
        InputProps={{
          className: classesLoc.input
        }}
      />
    </Box>
  );

  // Hide for draft stage regardless of role
  if (isDraft) {
    return null;
  }

  if (!canViewWireVerification(originatorName)) {
    return null;
  }

  const isAMRole = isDrawAMRole();
  // Wire verification dropdown in the Bank Details header is Draw Team only.
  // Originators see the wire verification badge in the alert area (DrawTabs.tsx) instead.
  if (!isAMRole) {
    return null;
  }
  const isReadOnly = !isAMRole;

  return (
    <Box
      sx={{
        display: "inline-flex",
        alignItems: "stretch",
        border: isReadOnly ? "none" : "1px solid #e2e8f0",
        borderRadius: "6px",
        overflow: "hidden",
        background: isReadOnly ? "transparent" : "#ffffff",
        position: "relative"
      }}
    >
      {/* Dropdown trigger — left side */}
      <Box
        onClick={isReadOnly ? undefined : handleChipClick}
        data-testid="wire-verification-control"
        sx={{
          display: "flex",
          alignItems: "center",
          gap: "6px",
          background: "transparent",
          borderRight: isReadOnly ? "none" : "1px solid #e2e8f0",
          padding: "4px 10px",
          cursor: isReadOnly ? "default" : "pointer",
          "&:hover": {
            background: isReadOnly ? "transparent" : "#f7fafc"
          }
        }}
      >
        <StatusIcon sx={{ fontSize: 14, color: isCompleted ? "#48bb78" : "#f6a609" }} />
        <span style={{ color: "#718096", fontSize: "13px" }}>Wire Verification:</span>
        <span style={{ color: "#2d3748", fontWeight: 500, fontSize: "13px" }}>{currentStatus}</span>
        {!isReadOnly && (
          <KeyboardArrowDownIcon sx={{ fontSize: 12, color: "#718096" }} />
        )}
      </Box>

      {/* History icon — right side, AM roles only */}
      {!isReadOnly && (
        <IconButton
          size="small"
          onClick={handleHistoryClick}
          data-testid="wire-verification-history-btn"
          sx={{
            background: "#5e72e4",
            borderRadius: 0,
            alignSelf: "stretch",
            minWidth: "28px",
            padding: "0 10px",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            "&:hover": { background: "#4a5fd4" }
          }}
        >
          <HistoryIcon sx={{ fontSize: 16, color: "#ffffff" }} />
        </IconButton>
      )}

      {/* Dropdown Menu */}
      <Menu
        anchorEl={anchorEl}
        open={menuOpen}
        onClose={handleMenuClose}
        anchorOrigin={{ vertical: "bottom", horizontal: "left" }}
        transformOrigin={{ vertical: "top", horizontal: "left" }}
        MenuListProps={{
          disablePadding: true
        }}
        PaperProps={{
          sx: {
            background: "#ffffff",
            border: "1px solid #e2e8f0",
            borderRadius: "6px",
            boxShadow: "0 4px 12px rgba(0,0,0,0.1)",
            minWidth: anchorEl ? anchorEl.offsetWidth : "auto",
            mt: "4px"
          }
        }}
      >
        <MenuItem
          onClick={() => handleStatusSelect("Pending")}
          selected={currentStatus === "Pending"}
          sx={{ fontSize: "13px", padding: "8px 12px", "&:hover": { background: "#f7fafc" } }}
        >
          <Box sx={{ display: "flex", alignItems: "center", gap: 1, width: "100%" }}>
            Pending
            {currentStatus === "Pending" && <CheckIcon fontSize="small" sx={{ ml: "auto" }} />}
          </Box>
        </MenuItem>
        <MenuItem
          onClick={() => handleStatusSelect("Completed")}
          selected={currentStatus === "Completed"}
          sx={{ fontSize: "13px", padding: "8px 12px", "&:hover": { background: "#f7fafc" } }}
        >
          <Box sx={{ display: "flex", alignItems: "center", gap: 1, width: "100%" }}>
            Completed
            {currentStatus === "Completed" && <CheckIcon fontSize="small" sx={{ ml: "auto" }} />}
          </Box>
        </MenuItem>
      </Menu>

      {/* History Popover */}
      <Popover
        open={historyOpen}
        anchorEl={historyAnchorEl}
        onClose={handleHistoryClose}
        anchorOrigin={{ vertical: "top", horizontal: "right" }}
        transformOrigin={{ vertical: "bottom", horizontal: "right" }}
        sx={{ mt: '-10px' }}
      >
        <Box sx={{ p: 2, minWidth: 300, maxWidth: 400, maxHeight: 350, overflowY: "auto" }}>
          <Typography variant="subtitle2" sx={{ fontWeight: "bold", mb: 1 }}>
            Wire Verification History
          </Typography>
          {historyLoading ? (
            <Typography variant="body2" color="text.secondary">Loading...</Typography>
          ) : historyData.length === 0 ? (
            <Typography variant="body2" color="text.secondary">No history available</Typography>
          ) : (
            historyData.map((item) => (
              <Box
                key={item.historyId}
                sx={{
                  display: "flex",
                  alignItems: "flex-start",
                  gap: 1.5,
                  mb: 1.5,
                  py: 0.5
                }}
              >
                <Box
                  sx={{
                    width: 24,
                    height: 24,
                    borderRadius: "50%",
                    background: "#5e72e4",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    flexShrink: 0,
                    mt: 0.5
                  }}
                >
                  <EditOutlinedIcon sx={{ fontSize: 16, color: "#ffffff" }} />
                </Box>
                <Box sx={{ flex: 1 }}>
                  <Typography
                    variant="caption"
                    sx={{
                      display: "block",
                      color: "#2d3748"
                    }}
                  >
                     <strong>Previous Value:</strong> {item.previousStatus || "Pending"}
                  </Typography>
                  <Typography
                    variant="caption"
                    sx={{
                      display: "block",
                      color: "#2d3748"
                    }}
                  >
                    <strong>Current Value:</strong> {item.newStatus}
                  </Typography>
                  {item.comment && (
                    <Typography variant="caption" display="block">
                      <strong>Reason:</strong> {item.comment}
                    </Typography>
                  )}
                  <Typography variant="caption" color="text.secondary">
                    {item.updatedBy} | {getDrawDateCreated(item.createdAt)}
                  </Typography>
                </Box>
              </Box>
            ))
          )}
        </Box>
      </Popover>

      {/* Confirmation Modal */}
      <ConfirmFormModal
        isOpen={confirmModalOpen}
        handleClose={() => {
          setConfirmModalOpen(false);
          setPendingStatus(null);
          setComment("");
        }}
        successHandler={handleConfirmStatusChange}
        containerStyle={{ overflow: "auto" }}
        header="Changing Wire Verification Status"
        body={confirmModalBody()}
        primaryBtnName="Yes, Change"
        confirmCondition={!comment.trim() || isSubmitting}
        size="sm"
        headerStyles={{
          padding: 14,
          borderBottom: "solid 1px #dddddd"
        }}
        footerStyles={{
          borderTop: "solid 1px #dddddd",
          padding: "0px 0px 3px 17px",
          margin: "0 0px 14px 0px"
        }}
      />
    </Box>
  );
};

export const BankDetailsWrapper = (props: any) => {
  const classes = useDrawsStyles();
  const { drawId } = useParams<any>() as any;
  const {
    selectedLoanId,
    isSubmitClicked,
    drawObj,
    isEditing,
    fieldWidth,
    setSnackBarMsg,
    setOpenSnackBar
  } = props;
  const isDraft = drawObj?.stage === DrawStage.draft;

  return (
    <Grid
      item
      container
      xs={12}
      className={classes.itemContainer}
      data-testid="bank-details-header"
    >
      <Grid
        item
        container
        xs={12}
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          paddingBottom: "4px"
        }}
      >
        <Grid item className={classes.itemHeaderText}>
          Bank Details
        </Grid>
        <WireVerificationControl
          status={drawObj?.wireVerificationStatus}
          drawId={drawId}
          originatorName={drawObj?.originatorName}
          setSnackBarMsg={setSnackBarMsg}
          setOpenSnackBar={setOpenSnackBar}
          isDraft={isDraft}
        />
      </Grid>
      <Grid
        item
        container
        xs={12}
        className={classes.fieldContainer}
        style={{ height: isOrigUser() ? "95%" : "85%" }}
      >
        <BankDetails
          selectedLoanId={selectedLoanId}
          isSubmitClicked={isSubmitClicked}
          drawObj={drawObj}
          isEditing={isEditing}
          fieldWidth={fieldWidth}
        />
      </Grid>
    </Grid>
  );
};